import * as layout from '../../../components/markLayout.module.css';
import Section from '@hero/ui-kit/layout/Section';
import MarkModal from '../../../components/MarkModal';
import { MarkButton, MarkHeading } from '../../../components/MarkControls';
import React, { useId, useState } from 'react';
import P from '@hero/ui-kit/typography/P';
import useUpdateDrip from '../../../api/useUpdateDrip';
import useDrip from '../../../api/useDrip';
import { useNotifications } from '../../../../../context/notification';
import { extractDripErrorMessage } from '../dripFormUtils';
import { getSendWindowErrors } from '../../../utils/sendWindow';
import Step2Audience from '../New/Step2Audience';
import type { Step2Data } from '../New/Step2Audience';
import Step3Schedule from '../New/Step3Schedule';
import type { Step3Data } from '../New/Step3Schedule';
import type { DripDetail, DripVersion, SegmentTargetKind } from '../../../types';
type Field = 'audience' | 'schedule';
type Props = {
    drip: DripDetail;
    field: Field;
    onClose: () => void;
    onSuccess: () => void;
    onScheduleSaved: (version: DripVersion) => void;
};
const VALID_TARGET_KINDS = new Set<string>([
    'PROFILE',
    'MEMBERSHIP',
    'MEMBERSHIP_ADMIN',
    'PROSPECT'
]);
const OverviewEdit: React.FC<Props> = ({ drip, field, onClose, onSuccess, onScheduleSaved }) => {
    const update = useUpdateDrip();
    const { refetch } = useDrip(field === 'schedule' ? drip.drip_id : 0);
    const [preSaveChecking, setPreSaveChecking] = useState(false);
    const [preSaveError, setPreSaveError] = useState<string | null>(null);
    const isSaving = update.isPending || preSaveChecking;
    const { addNotification } = useNotifications();
    const headingId = useId();
    // Keep the modal-opening base even when preflight refreshes the parent query.
    const [scheduleBase] = useState(drip);
    const version = scheduleBase.draft_version ?? scheduleBase.current_version;
    const [audience, setAudience] = useState<Step2Data>({
        enrollmentSegmentId: drip.enrollment_segment_id,
        enrollmentSegmentName: drip.enrollment_segment_name,
        reEntryPolicy: drip.re_entry_policy,
        reEntryDays: drip.re_entry_days ?? 1
    });
    const [schedule, setSchedule] = useState<Step3Data>({
        sendWindowStart: version?.send_window_start ?? '',
        sendWindowEnd: version?.send_window_end ?? ''
    });
    // Enrollment determines the drip's target_kind, so unlike a check_segment
    // step's picker (which also accepts PROFILE), this filter must be exact —
    // spec 2026-09-16-mark-backoffice-plan.md:2927. With no segment yet the BE
    // reports a PROFILE fallback (validation.py drip_target_kind), not a
    // choice, so the first pick may be any kind.
    const hasEnrollmentSegment = drip.enrollment_segment_id != null;
    const filterTargetKinds =
        hasEnrollmentSegment && drip.target_kind && VALID_TARGET_KINDS.has(drip.target_kind)
            ? [drip.target_kind as SegmentTargetKind]
            : undefined;
    const oneSided = schedule.sendWindowStart.length > 0 !== schedule.sendWindowEnd.length > 0;
    const hasEmail = (version?.steps ?? []).some(
        (s) => s.type === 'activity' && s.activity === 'send_email'
    );
    const hasSms = (version?.steps ?? []).some(
        (s) => s.type === 'activity' && s.activity === 'send_sms'
    );
    const channelErrors = oneSided
        ? []
        : getSendWindowErrors(hasEmail, hasSms, schedule.sendWindowStart, schedule.sendWindowEnd);
    const scheduleInvalid = oneSided || channelErrors.length > 0;
    const initialStart = version?.send_window_start ?? '';
    const initialEnd = version?.send_window_end ?? '';
    const startChanged = schedule.sendWindowStart !== initialStart;
    const endChanged = schedule.sendWindowEnd !== initialEnd;
    const scheduleUnchanged = !startChanged && !endChanged;
    const handleSave = async () => {
        if (isSaving) return;
        if (field === 'audience') {
            update.mutate(
                {
                    drip_id: drip.drip_id,
                    enrollment_segment_id: audience.enrollmentSegmentId,
                    re_entry_policy: audience.reEntryPolicy,
                    re_entry_days:
                        audience.reEntryPolicy === 'once_per_n_days' ? audience.reEntryDays : null
                },
                {
                    onSuccess: () => {
                        addNotification({ type: 'success', message: 'Audience settings saved.' });
                        onSuccess();
                    }
                }
            );
        } else {
            if (scheduleInvalid) return;
            if (scheduleUnchanged) {
                onClose();
                return;
            }
            const body = {
                drip_id: drip.drip_id,
                ...(startChanged ? { send_window_start: schedule.sendWindowStart || null } : {}),
                ...(endChanged ? { send_window_end: schedule.sendWindowEnd || null } : {})
            };
            setPreSaveError(null);
            setPreSaveChecking(true);
            const latest = await refetch().finally(() => setPreSaveChecking(false));
            const fresh = latest.data?.data;
            if (latest.isError || !fresh) {
                setPreSaveError('Could not re-check the drip before saving. Try again.');
                return;
            }
            if (
                fresh.status !== scheduleBase.status ||
                (fresh.draft_version?.version_number ?? null) !==
                    (scheduleBase.draft_version?.version_number ?? null) ||
                (fresh.current_version?.version_number ?? null) !==
                    (scheduleBase.current_version?.version_number ?? null) ||
                JSON.stringify(fresh.draft_version ?? fresh.current_version) !==
                    JSON.stringify(version)
            ) {
                setPreSaveError(
                    'Not saved — this drip changed since you opened the schedule editor. Your edits are kept. Close and reopen the editor to load the latest version.'
                );
                return;
            }
            update.mutate(body, {
                onSuccess: (response) => {
                    const newVersion = response.data.draft_version ?? response.data.current_version;
                    if (newVersion) onScheduleSaved(newVersion);
                    addNotification({
                        type: 'success',
                        message: response.data.new_version_created
                            ? `Send window saved — draft version v${response.data.draft_version?.version_number} created.`
                            : 'Send window saved.'
                    });
                    onSuccess();
                }
            });
        }
    };
    return (
        <MarkModal
            onClose={onClose}
            isPending={isSaving}
            aria-labelledby={headingId}
            styles={{
                width: 'min(64rem, 90vw)'
            }}
        >
            <Section noDefaultPadding className={layout.form}>
                <MarkHeading
                    role="h4"
                    id={headingId}
                    tabIndex={-1}
                    style={{
                        margin: '0 0 1rem'
                    }}
                >
                    {field === 'audience' ? 'Edit audience & policy' : 'Edit send window'}
                </MarkHeading>
                <fieldset
                    disabled={isSaving}
                    style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}
                >
                    {field === 'audience' ? (
                        <Step2Audience
                            data={audience}
                            onChange={setAudience}
                            triggerType={drip.trigger_type}
                            errors={[]}
                            filterTargetKinds={filterTargetKinds}
                        />
                    ) : (
                        <Step3Schedule
                            data={schedule}
                            onChange={setSchedule}
                            errors={
                                oneSided
                                    ? ['Set both start and end, or leave both empty.']
                                    : channelErrors
                            }
                        />
                    )}
                </fieldset>
                {(preSaveError || update.error) && (
                    <div
                        role="alert"
                        style={{
                            marginTop: '0.8rem'
                        }}
                        className={layout.notice}
                    >
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-danger)'
                            }}
                        >
                            {preSaveError ?? extractDripErrorMessage(update.error!)}
                        </P>
                    </div>
                )}
                <div
                    style={{
                        marginTop: '1.6rem'
                    }}
                    className={layout.actions}
                >
                    <MarkButton type="button" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </MarkButton>
                    <MarkButton
                        type="button"
                        onClick={handleSave}
                        disabled={
                            isSaving ||
                            (field === 'schedule' && (scheduleInvalid || scheduleUnchanged))
                        }
                        variant="primary"
                    >
                        {isSaving ? 'Saving…' : 'Save'}
                    </MarkButton>
                </div>
            </Section>
        </MarkModal>
    );
};
export default OverviewEdit;
